/**
 * **The screen-entrance motion dictionary.** One curve, one duration table, one cascade rule for
 * the whole app, so no two surfaces settle at different speeds.
 *
 * ── Where these numbers come from ────────────────────────────────────────────────────────────
 * Transcribed from the owner's interactive handoff, `Detal klienta v3 - interaktywny.dc.html`
 * (its `<helmet>` keyframe block and the `anim()` helper at its line 782). Nothing here was
 * chosen by a developer: every duration, every base delay and every cascade step is the
 * handoff's own value. The handoff names its steps `dcEnter`, `dcRise`, `dcPop` and so on; the
 * names below drop the `dc` prefix and nothing else, so a reader can diff the two by eye.
 *
 * ONE entry is not the handoff's, and it says so at its own line: `dayStep`, the day panel's step
 * from one day to the next, which the owner re-timed by hand on 9 September 2026 after calling the
 * mock's own value dramatic. It lives here rather than in the feature because this table is the
 * app's single curve-and-duration dictionary, and a second one would be the near-duplicate the
 * dictionary exists to prevent.
 *
 * The handoff uses `dcGrowX` at two settings — 260ms/0ms on a bar cell, 620ms/220ms on the wide
 * meter. The wide one is what this app draws and it is what the table encodes; the short one is
 * recorded here rather than silently averaged, because an average would be a value the handoff does
 * not contain. A caller that ever needs it can shift the start with `delay`.
 *
 * ── Only the steps this app DRAWS are in the table ───────────────────────────────────────────
 * The handoff also defines `dcEnter2` (geometrically identical to `dcEnter`; it exists so adjacent
 * panels do not move in lockstep), `dcFade`, `dcPop` and `dcRing`. None of them is used by any
 * surface here, and all four were removed on 2 September 2026 — a dictionary entry nothing looks up
 * is a claim about this app's motion that is not true of it. `dcRing` in particular read as the
 * mechanism behind the rings, which the vendored bklit charts have always drawn themselves.
 *
 * ── `draw` is the one deliberate substitution, and it is not a compromise ────────────────────
 * The handoff draws a line by animating `stroke-dashoffset` from `var(--len)` to `0`, which needs
 * the path's measured length in a custom property. Motion implements exactly that effect natively
 * through `pathLength`: it sets `pathLength`, `strokeDasharray` and `strokeDashoffset` on the
 * element itself, so the measurement stops being the caller's problem. Same visual, same
 * duration, same curve — the mechanism moves into the library. Recorded because the property
 * name in the DOM will not match the handoff's.
 *
 * ── Reduced motion is enforced HERE, not by the stylesheet ───────────────────────────────────
 * `tokens/base.css` collapses `animation-duration` under `prefers-reduced-motion: reduce`, and
 * that rule **does not reach anything on this page**: Motion drives the Web Animations API and
 * inline style properties, neither of which a CSS `animation-duration` declaration governs. A
 * surface that animated through Motion and relied on that block would ignore the preference
 * completely while looking, in the stylesheet, as though it honoured it.
 *
 * So the preference is a parameter of this module. `reducedMotion: true` returns a step whose
 * start state IS its end state, with zero duration and zero delay — the same outcome the
 * handoff's `.01ms` override produces, reached deterministically instead of by a race. It is a
 * parameter rather than a hook call because this module is `model`-shaped: no React, no DOM, no
 * media query. `useScreenMotion` reads the preference and passes it in.
 */

/**
 * The one easing curve the handoff applies to every entrance on the screen
 * (`const EASE = 'cubic-bezier(.22,.61,.36,1)'`).
 *
 * NOTE for whoever reaches for a token instead: this is **not** `--ease` in
 * `tokens/spacing.css`, which is `cubic-bezier(0.22, 1, 0.36, 1)`. The two share three control
 * values and differ in the second — 0.61 against 1 — which is the difference between a settle and
 * an overshoot-free glide. `--ease-screen-entrance` in `tokens/spacing.css` carries this curve for
 * CSS transitions; the array below is the same curve for Motion, which takes numbers.
 */
export const SCREEN_EASE = [0.22, 0.61, 0.36, 1] as const;

/**
 * The plan library inspector's own curve, `cubic-bezier(.2,.8,.3,1)`.
 *
 * The one step in this dictionary that is not an ENTRANCE. `Programy v2 - D3 (0.8)` writes it for
 * the inspector's swap — the pane exchanging one plan for another under a moving pointer — and it
 * is a snappier curve than `SCREEN_EASE` deliberately: an entrance may take its time because it
 * happens once, and a swap that took the same 340ms would still be settling when the trainer has
 * moved two rows on. Recorded as a second constant rather than folded into the first, because they
 * answer different questions and averaging them would be a value neither handoff contains.
 */
export const SWAP_EASE = [0.2, 0.8, 0.3, 1] as const;

/**
 * The curve a closing gesture collapses a box on.
 *
 * It is the mirror of `SCREEN_EASE`: an entrance decelerates into place because the eye has to
 * read where it landed, and a departure ACCELERATES away because there is nothing left to read.
 * Used by the departures below and by nothing else — a surface reaching for it to arrive with
 * would be arriving backwards.
 *
 * ── It is NOT the mock's `--ease-exit`, and the difference is measured ───────────────────────
 * `docs/design/2026-09-16-zamykanie-zadania.html` writes `--ease-exit: cubic-bezier(.4,0,.7,.2)`, and this was that
 * curve byte for byte until 16 September 2026. The mock uses it on a box that is still there
 * afterwards; ours is REMOVED the instant its tween ends, and that makes the tail of the curve the
 * whole of the gesture's last frame.
 *
 * Measured on `cubic-bezier(.4,0,.7,.2)`: both of its control points sit low, so at **91% of the
 * duration 22% of the height is still standing** — 18.4px of an 87px row — and the element is gone
 * one frame later. The gap did not close, it snapped shut. Raising the second control point to
 * `(.2, 1)` keeps the accelerate-away opening (`.4, 0` is unchanged, which is the half of the mock's
 * curve that carries its meaning) and leaves **0.5% at the same 91%**: under half a pixel on the
 * last frame anything is painted, which is the difference between a gap closing and a jump.
 */
export const EXIT_EASE = [0.4, 0, 0.2, 1] as const;

/**
 * **The one overshooting curve in this dictionary, and the only one there will be.**
 *
 * `cubic-bezier(.34,1.56,.64,1)` — the mock's `--ease-spring`, byte for byte. Its second control
 * point is above 1, so the value it drives goes PAST its target and settles back: a mark that lands
 * rather than one that appears.
 *
 * It is allowed on exactly one thing, the closing sign's glyph, and the mock's own spec says why
 * the exception has to be written down: overshoot on a 40-76px mark inside a plate reads as the
 * mark landing, while overshoot on a row of a list reads as the list being unstable. The glyph is
 * also the only place it is SAFE — the slot around it is `overflow: hidden` while it leaves, so a
 * plate springing past its own edges would be clipped on its peak frame.
 *
 * If a second surface ever wants it, that is a design decision and not a reuse.
 */
export const SPRING_EASE = [0.34, 1.56, 0.64, 1] as const;

/**
 * No easing at all, for a value that has nothing to characterise.
 *
 * A plate's opacity is one of those: it is the ground a mark stands on, and a ground that eases in
 * is a ground drawing attention to its own arrival. The mock writes `linear` for it and this is the
 * same statement in the shape this dictionary takes.
 */
const LINEAR_EASE = [0, 0, 1, 1] as const;

/**
 * The handoff's entrance steps, by its own names minus the `dc` prefix, plus `dayStep`, plus the
 * four steps the queue card's closing mock adds (`markIn`, `tick`, `signPlate`, `signGlyph`).
 */
export type ScreenMotionName =
  | 'enter'
  | 'rise'
  | 'draw'
  | 'growX'
  | 'cell'
  | 'swap'
  | 'dayStep'
  | 'markIn'
  | 'tick'
  | 'signPlate'
  | 'signGlyph'
  | 'photoSwap'
  | 'capRise'
  | 'calendarView'
  | 'calendarForward'
  | 'calendarBack';

/** Every name in the entrance table, so a test can cover the table rather than a copy of it. */
export const SCREEN_MOTION_NAMES: readonly ScreenMotionName[] = [
  'enter',
  'rise',
  'draw',
  'growX',
  'cell',
  'swap',
  'dayStep',
  'markIn',
  'tick',
  'signPlate',
  'signGlyph',
  'photoSwap',
  'capRise',
  'calendarView',
  'calendarForward',
  'calendarBack',
];

/**
 * A property bag Motion accepts as `initial` / `animate`.
 *
 * An array is a KEYFRAME list — one step in this table needs it (`signPlate`, which is a whole life
 * rather than an arrival), and Motion reads `transition.times` alongside it.
 */
type MotionState = Readonly<Record<string, number | number[]>>;

/** One cubic-bezier, as Motion takes it. */
export type Curve = readonly [number, number, number, number];

/**
 * A step's easing: one curve for the whole tween, or **one curve per keyframe segment**.
 *
 * The difference is not a refinement, it is the difference between the table's numbers being played
 * and being distorted. Motion runs opacity and transform through the Web Animations API
 * (`startWaapiAnimation`), where a SINGLE curve becomes the effect's own `easing` — applied across
 * the whole iteration, with `times` only mapping the eased progress onto the keyframes afterwards.
 * An ARRAY is written onto the keyframes instead, and each segment then takes its own curve for its
 * own stretch of the duration.
 *
 * The array is mutable where every other value here is readonly, and that is the LIBRARY's
 * requirement rather than a slip: Motion types a per-segment easing as `Easing[]`, and a
 * `readonly` one is refused at every call site a step is spread onto. Nothing mutates it.
 *
 * Measured on the sign's life before it was fixed, 16 September 2026: a 200ms life written as 70 in / 60
 * held / 70 out reached full opacity **22ms** after it appeared and was already at 0.87 by 56ms —
 * a single global ease-out had eaten the entrance and spread the exit over the rest. The table said
 * one thing and the browser played another.
 */
export type StepEase = Curve | Curve[];

export interface ScreenMotionStep {
  readonly initial: MotionState;
  readonly animate: MotionState;
  readonly transition: {
    readonly duration: number;
    readonly delay: number;
    readonly ease: StepEase;
    /** Where each keyframe of an ARRAY target sits, 0 to 1. Absent for every ordinary step. */
    readonly times?: number[];
  };
}

export interface ScreenMotionOptions {
  /**
   * Whether the viewer asked for reduced motion. Required, and deliberately not optional: a
   * default would let a caller forget it and ship an animation that ignores the preference,
   * which is the failure this parameter exists to prevent.
   */
  readonly reducedMotion: boolean;
  /** Position in a cascade, zero-based. Omitted means "not part of a cascade". */
  readonly index?: number;
  /** Override the handoff base delay, in milliseconds. */
  readonly delay?: number;
}

interface StepSpec {
  readonly initial: MotionState;
  readonly animate: MotionState;
  /** Milliseconds. */
  readonly duration: number;
  /** Milliseconds before the first item of a cascade moves. */
  readonly baseDelay: number;
  /** Milliseconds added per cascade position. */
  readonly cascade: number;
  /** The step's own curve. Absent means `SCREEN_EASE`, which is every ENTRANCE. */
  readonly ease?: StepEase;
  /** Where each keyframe of an ARRAY target sits, 0 to 1. Only a keyframed step has one. */
  readonly times?: number[];
}

/**
 * **The sign's life, in its three parts** — the one step in this table that is a whole life rather
 * than an arrival, split here because the ROW's departure is timed off it (see `DEPARTURES`).
 *
 * Under variant P2 the sign stands in the leaving row's own place, so the box may not begin to close
 * until the sign has arrived AND stood still long enough to be read. `screenDeparture` reads
 * `in + hold` to know when that moment is. Two numbers in two files would be the near-duplicate this
 * dictionary exists to prevent, and the one that drifts silently.
 */
const SIGN = {
  /** `symDelay` — the sign lands INSIDE the content's own fade, which is what "Podmiana" names. */
  delay: 45,
  /** `symIn * .55` — the plate's fade-in. Shorter than the glyph's arrival, on purpose: the ground
   *  is there before the mark finishes landing on it. */
  plateIn: 72,
  /** What is left between the plate reaching full strength and the box starting to close. */
  plateHold: 8,
  /** `boxMs * .65` — the plate goes out UNDER the closing box rather than before it. */
  plateOut: 78,
  /** `symIn` — the glyph's spring. */
  glyphMs: 130,
  /** The mock's `scale(.86)`, restored: this is the overshoot's own object. */
  glyphFrom: 0.86,
} as const;
const SIGN_PLATE_MS = SIGN.plateIn + SIGN.plateHold + SIGN.plateOut;

/**
 * **The mock's P2 timing for a row that has a sign to show** — `rowMs: 90`, `boxDelay: 125`,
 * `boxMs: 120`.
 *
 * The box starts closing while the plate is still at full strength, and the plate fades out under
 * it. That is the mock, and it is the opposite of the version built first, which held the box open
 * for the sign's whole life and cost 431ms for a gesture the mock plays in 245.
 *
 * `collapseDelay` is DERIVED, so the two cannot drift: the box is released in the frame the plate
 * starts to go.
 */
const SIGNED = {
  contentMs: 90,
  collapseDelay: SIGN.delay + SIGN.plateIn + SIGN.plateHold,
} as const;

/**
 * The table. Every number is the handoff's; see the file header for the step it uses at two
 * settings, for why `draw` reads `pathLength` where the handoff reads `stroke-dashoffset`, and for
 * the four steps the handoff defines that no surface here draws.
 */
const STEPS: Readonly<Record<ScreenMotionName, StepSpec>> = {
  // @keyframes dcEnter — anim('dcEnter', 340, 0)
  enter: { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, duration: 340, baseDelay: 0, cascade: 0 },
  // @keyframes dcRise — anim('dcRise', 300, 120 + i * 34)
  rise: { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, duration: 300, baseDelay: 120, cascade: 34 },
  // @keyframes dcDraw — anim('dcDraw', 900, 120)
  draw: { initial: { pathLength: 0 }, animate: { pathLength: 1 }, duration: 900, baseDelay: 120, cascade: 0 },
  // @keyframes dcGrowX — anim('dcGrowX', 620, 220)
  growX: { initial: { scaleX: 0 }, animate: { scaleX: 1 }, duration: 620, baseDelay: 220, cascade: 0 },
  // @keyframes dcCell — anim('dcCell', 260, 120 + i * 26)
  cell: { initial: { opacity: 0, scale: 0.6 }, animate: { opacity: 1, scale: 1 }, duration: 260, baseDelay: 120, cascade: 26 },
  // KANON `Programy v2 - D3 (0.8)`: `@keyframes swapA` / `swapB` — `opacity .15 -> 1`,
  // `translateY(5px) -> none`, applied as `.26s cubic-bezier(.2,.8,.3,1) both`. FIVE pixels, not
  // six: this is the x0,8 file and the designer rounded 6 x 0,8 up to the nearest half pixel.
  //
  // It starts at .15 rather than 0 because the pane never goes blank — the outgoing plan is
  // replaced in the same frame, so a fade from nothing would read as a flash of empty card between
  // two rows. The specimen alternates two identical keyframes to make the browser replay the
  // animation; React remounts on a changed `key` instead, which is the same trick with one name.
  swap: { initial: { opacity: 0.15, y: 5 }, animate: { opacity: 1, y: 0 }, duration: 260, baseDelay: 0, cascade: 0, ease: SWAP_EASE },
  // The ONE step in this table that no handoff contains, and the owner's own instruction rather
  // than a developer's taste — 9 September 2026: "NAPRAW animacje przełączania się dni
  // (wskakiwania wyskakiwania ćwiczeń), bo aktualnie jest dramatyczna, a ma być smooth i szybka".
  //
  // What it replaces is `Builder planu - tydzien - programator`'s own `swapA`/`swapB` at .32s from
  // `opacity .2`, applied to the day panel's whole scroll body as one block. Three things made that
  // read as a lurch: five cards blinking together, a start low enough to read as a WIPE rather than
  // a change of contents, and 320ms of it. So this is the mock's curve at the mock's shape, moved
  // to the CARDS and re-timed to the owner's brief:
  //
  //   · 180ms, not 320 — "szybka".
  //   · from 0.5, not 0.2 — the arriving day is legible in its first frame; nothing is hidden
  //     behind the animation, which is the house rule the .2 start was already straining.
  //   · 4px, not 6 — a rise the eye reads as arriving, at a size no reader has to wait out.
  //   · a 22ms cascade, which the mock has no equivalent for: it fades one block and this fades a
  //     list, and a list that arrives in one blink is the "wskakiwanie" the owner named.
  //
  // The curve is NOT re-chosen: `SWAP_EASE` is `cubic-bezier(.2,.8,.3,1)`, which is the curve the
  // programator mock puts on every transition it has, `--ease-fp` byte for byte.
  //
  // The cascade's CAP is the caller's, not the table's — see `lib/useDayStepMotion`. A dictionary
  // entry cannot know how many cards a day holds, and `index` is what a caller clamps.
  dayStep: { initial: { opacity: 0.5, y: 4 }, animate: { opacity: 1, y: 0 }, duration: 180, baseDelay: 0, cascade: 22, ease: SWAP_EASE },

  // ── The queue card's closing gestures ──────────────────────────────────────────────────────
  // The three entries below and the two departures under them are NOT the client-detail handoff's.
  // They are `docs/design/2026-09-16-zamykanie-zadania.html`, the owner's approved mock of 16 September 2026 for
  // closing a task on the trainer's home — variant **P2 "Podmiana"** for one item (the owner
  // reversed his own choice of P3 on 17 September 2026; `docs/design/README.md` carries the
  // measurements that decided it) and W1 "Ptaszek się rysuje" for the card that has run out of
  // them. They live here for the reason
  // `dayStep` does: this table is the app's one curve-and-duration dictionary, and a second one in
  // the feature would be the near-duplicate the dictionary exists to prevent.
  //
  // W1's mark, which appears WHOLE and quietly rather than being drawn: the circle is there from
  // the first frame and only the tick inside it is written. The mock's own 200ms / scale .94.
  markIn: { initial: { opacity: 0, scale: 0.94 }, animate: { opacity: 1, scale: 1 }, duration: 200, baseDelay: 0, cascade: 0 },
  // The tick being written into that circle — `draw`'s mechanism at the mock's tempo. A SECOND
  // draw rather than `draw` with an override, because 900ms is a value the line CHART owns (see
  // the header) and a 44px check written at chart speed reads as a chart loading. 220ms from the
  // 140th, so the circle is settled before anything is written in it.
  tick: { initial: { pathLength: 0 }, animate: { pathLength: 1 }, duration: 220, baseDelay: 140, cascade: 0 },
  // ── The sign, and why it is TWO steps ──────────────────────────────────────────────────────
  // Rebuilt 17 September 2026 to the mock's own P2, after the owner looked at the built version and
  // said it was not what the artefact drew: "przecież P2 w animacjach w artefakcie wyglądało
  // inaczej niż to co zaimplementowałeś". It was not. The mock
  // (`docs/design/2026-09-16-zamykanie-zadania.html`, `symbolOut`, `place: 'replace'`) animates two
  // elements with two different curves, and collapsing them into one square badge on one tween lost
  // the gesture:
  //
  // |  | the mock's P2 | what was built | what it looked like |
  // |---|---|---|---|
  // | the plate | the row's FULL box — `inset-inline: 0`, the row's own height | a 74-110px square, centred | a badge appearing in a row, not a row becoming a mark |
  // | the glyph | `scale .86 -> 1` on a spring, `cubic-bezier(.34,1.56,.64,1)` | `scale .96 -> 1`, no spring | nothing arrived; it faded up |
  // | the whole gesture | **245ms**, box closing while the plate is still lit | **431ms**, with a 90ms hold first | slow, and a beat of nothing in the middle |
  //
  // So: the PLATE only ever changes `opacity`, linearly, and it is the row-sized ground. The GLYPH
  // only ever changes `scale`, on the spring. The overshoot is therefore on a 40-76px mark sitting
  // well inside a plate, not on the plate's own edges — which is what makes it safe inside a slot
  // that is `overflow: hidden` while it leaves, and which was the stated reason the spring had been
  // dropped. The reason was real; the fix was to put the spring where the mock puts it.
  //
  // `SPRING_EASE` is the one overshooting curve in this dictionary and it stays the only one. The
  // mock's own spec calls it "wyjątek od reguły «jeden tween, jedna krzywa»" and asks for exactly
  // this comment.
  //
  // ── The numbers are the mock's, to the millisecond ─────────────────────────────────────────
  // `t: { rowMs: 90, symDelay: 45, symIn: 130, boxDelay: 125, boxMs: 120 }`. Everything below is
  // derived from `SIGN` rather than restated, so the plate's life and the box's delay cannot drift
  // apart — they are the same three numbers read twice.
  //
  // `times` are literal ONLY because `ease` is an array — see `StepEase`. With one curve the
  // browser eases the whole iteration and these numbers stop describing what is played. MEASURED on
  // this step before that was understood: a 200ms life written as 70/60/70 reached full opacity
  // 22ms in and spent the rest fading.
  signPlate: {
    initial: { opacity: 0 },
    animate: { opacity: [0, 1, 1, 0] },
    duration: SIGN_PLATE_MS,
    baseDelay: SIGN.delay,
    cascade: 0,
    times: [0, SIGN.plateIn / SIGN_PLATE_MS, (SIGN.plateIn + SIGN.plateHold) / SIGN_PLATE_MS, 1],
    // Linear in all three segments, exactly as the mock writes it: a plate has no arrival to
    // characterise, it is either there or it is not.
    ease: [LINEAR_EASE, LINEAR_EASE, LINEAR_EASE],
  },
  signGlyph: {
    initial: { scale: SIGN.glyphFrom },
    animate: { scale: 1 },
    duration: SIGN.glyphMs,
    baseDelay: SIGN.delay,
    cascade: 0,
    ease: SPRING_EASE,
  },

  // ── The auth pane exchanging one photograph for another ────────────────────────────────────
  // The sign-up hero's journey (`shared/lib/authHeroJourney.ts`): the arrival photograph on the
  // role screen becomes the work photograph on every screen after it, and the pane is ONE physical
  // surface across those steps rather than a picture being replaced.
  //
  // The mockup's `photoIn`, byte for byte: `opacity 0, y 12 -> 1, 0` at `520ms var(--swap) both`,
  // against a `photoOut` that only fades. So the incoming photograph comes up over the outgoing one
  // AND travels the first half of the parallax — 12px for the photograph, 0 for the frame, 20 for
  // the caption (`capRise` below) — while the frame it stands in never moves. The travel is small
  // enough not to read as a slideshow and is clipped by the frame's own `overflow: hidden`, so the
  // trainer keeps looking at the same window onto the same gym.
  //
  // Only the incoming element carries this step. The outgoing one is static under it and is dropped
  // the frame it is fully covered (`shared/ui/HeroPhoto.tsx`), which is `photoOut` arriving at the
  // same place by a shorter road.
  //
  // 520ms, which is long against everything above it and deliberately so: those steps arrive INTO
  // an empty box and this one replaces something already being looked at. `SWAP_EASE` for the
  // reason `swap` has it — this is an exchange, not an entrance, and `SCREEN_EASE`'s long settle
  // would leave the outgoing frame half-visible for most of the gesture.
  photoSwap: { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, duration: 520, baseDelay: 0, cascade: 0, ease: SWAP_EASE },

  // ── The poster's caption arriving under its frame ──────────────────────────────────────────
  // `docs/design/rejestracja-premium-2026-09-21/d3-plakat-dark-losowanie.html`'s `capRise`, byte
  // for byte: `opacity 0, y 20 -> 0, 1` at `460ms var(--screen) 60ms both`. It is the second half
  // of the mockup's parallax at a step change — the photograph travels 12px (`photoSwap` above),
  // the frame 0, the caption 20 — so the two numbers belong to one gesture and this table holds
  // both. Longer and further than any entrance above it because it is the largest type on the
  // screen and the eye follows it rather than catching it.
  capRise: { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, duration: 460, baseDelay: 60, cascade: 0 },

  // ── The booking grid changing what it shows ────────────────────────────────────────────────
  // Owner, 25 September 2026: "Google Calendar style — fast, delicate, never waiting". The new
  // view arrives already legible — from 40% opacity, never from blank — in 150ms on the swap
  // curve, which is an ease-out; a page forward or back adds a 6px drift from the side it came
  // from, a view change (week ↔ month) none. Nothing waits for it and it waits for nothing: the
  // grid is drawn at once and only its ink settles.
  calendarView: { initial: { opacity: 0.4 }, animate: { opacity: 1 }, duration: 150, baseDelay: 0, cascade: 0, ease: SWAP_EASE },
  calendarForward: { initial: { opacity: 0.4, x: 6 }, animate: { opacity: 1, x: 0 }, duration: 150, baseDelay: 0, cascade: 0, ease: SWAP_EASE },
  calendarBack: { initial: { opacity: 0.4, x: -6 }, animate: { opacity: 1, x: 0 }, duration: 150, baseDelay: 0, cascade: 0, ease: SWAP_EASE },
};

/** The two ways an answered row leaves — see `screenDeparture`. */
export type ScreenDepartureName = 'rowDone' | 'rowDefer';

export interface ScreenDepartureOptions {
  readonly reducedMotion: boolean;
  /**
   * A sign stands in this row's place while it goes, so the box may not close underneath it.
   *
   * Required rather than defaulted, for the reason `reducedMotion` is: the two timings are one
   * choreography, and a caller who forgot this would get a box that shuts 210ms before the sign it
   * is holding open has finished — which is a defect nothing else in the system would report.
   * Whether a row HAS a sign is `model/queueClosing`'s rule, not this module's.
   */
  readonly signed: boolean;
}

/** One of the two elements a departure moves, ready to spread onto a Motion component. */
export interface ScreenDepartureTarget {
  /** The state it animates to as it goes. Spread onto `exit`. */
  readonly exit: MotionState;
  /**
   * Where it STANDS — every property the exit touches, at the value the stylesheet gives it.
   *
   * Part of the step rather than the caller's to write, because it is the answer to a case that has
   * nothing to do with taste: a row the server refuses comes back, and if it comes back while its
   * own exit is still running, Motion keeps the same element and animates it to whatever `animate`
   * says. Without this it would say nothing, and the restored row would keep the inline `height: 0`
   * its departure had reached — a task that is back on the list and 0px tall.
   */
  readonly rest: Readonly<Record<string, number | string>>;
  readonly transition: Tween;
}

export interface ScreenDepartureStep {
  /**
   * **The row's own content going**, and the outer box it goes inside — two elements, not one.
   *
   * They were one `motion.li` until 17 September 2026, when the sign moved INTO the leaving row
   * (variant P2). A sign painted inside an element whose opacity is on its way to zero is painted
   * at that element's opacity: measured on the first build of P2, the plate never rose above
   * **0.017** composite and the entire gesture was invisible. So the fade, the lift and the
   * fractional shrink belong to the box holding the row's own content, and the slot around it does
   * nothing but close — which is also what the mock draws, and which leaves the sign standing in a
   * container that is not fading under it.
   */
  readonly content: ScreenDepartureTarget;
  /** The slot closing under it, which is the whole of how the gap shuts. */
  readonly box: ScreenDepartureTarget;
  /**
   * **How the sign that replaces this row lives**, or `null` when the row leaves alone.
   *
   * Two steps, because the mock animates two elements: the row-sized plate only ever changes
   * `opacity`, the mark on it only ever changes `scale`, and they run on different curves. See the
   * table beside `signPlate` for what collapsing them into one cost.
   *
   * They are part of the departure rather than a second lookup at the call site because the three
   * are one choreography: the box's delay is derived from the plate's, so a caller cannot shorten
   * one without the other and leave a box closing over a plate at full strength.
   */
  readonly signLife: ScreenSignLife | null;
}

/** The closing sign's two halves — see `ScreenDepartureStep.signLife`. */
export interface ScreenSignLife {
  /** The row-sized ground: `opacity`, linear, appearing and going again. */
  readonly plate: ScreenMotionStep;
  /** The mark on it: `scale`, on `SPRING_EASE`, arriving once and staying. */
  readonly glyph: ScreenMotionStep;
}

interface Tween {
  readonly duration: number;
  readonly delay: number;
  readonly ease: readonly [number, number, number, number];
}

interface DepartureSpec {
  /** Where the content goes, and how long it takes. Milliseconds. */
  readonly exit: MotionState;
  readonly contentMs: number;
  /**
   * When the row's own box starts to close, for a row leaving with NOTHING to show. Milliseconds.
   *
   * A row that draws a sign takes `SIGNED.collapseDelay` instead, which is the mock's and is
   * derived from the plate's own life.
   */
  readonly collapseDelay: number;
}

/**
 * The mock's "C — Kierunek", re-timed on the owner's instruction: **the two acts differ by the
 * SIGN of a 5-to-8 pixel move**, so the trainer reads which one they pressed out of the corner of
 * an eye. "Gotowe" lifts and shrinks a fraction — the task is finished and rises off the list.
 * "Później" slides down — it is coming back.
 *
 * ── The numbers, and which of them are the mock's ────────────────────────────────────────────
 *
 * | | content | box starts | box closes | whole gesture |
 * |---|---|---|---|---|
 * | `rowDone`, alone | 120ms | 20ms | 120ms | **140ms** |
 * | `rowDefer`, alone | 140ms | 40ms | 120ms | **160ms** |
 * | `rowDone`, signed | 90ms | 125ms | 120ms | **245ms** |
 * | `rowDefer`, signed | 90ms | 125ms | 120ms | **245ms** |
 *
 * The two SIGNED rows are the mock's P2 to the millisecond (`rowMs: 90`, `boxDelay: 125`,
 * `boxMs: 120`), which is what the owner approved and what a first build of P2 did not deliver: it
 * held the box open for the sign's whole life and took **431ms** for this. The sign does not hold
 * the box open at all — the box closes while the plate is still lit, and the plate fades out under
 * it. See `SIGN` and `signPlate`.
 *
 * The two acts stop differing in TIME when a sign is drawn, and that is deliberate: under P2 the
 * thing that tells the trainer which button they pressed is the mark itself, a check or a clock at
 * 40-76px, so a 20ms difference in the fade underneath it is carrying nothing. The acts still
 * differ by the SIGN of a 5-to-8 pixel move, which is the owner's own instruction and is untouched:
 * "Gotowe" lifts and shrinks a fraction, "Później" slides down — it is coming back. Alone, with no
 * mark to read, the 20ms stays, because there the movement is all there is.
 *
 * ── What P2 costs, and what it does not ──────────────────────────────────────────────────────
 * **What waits is the gap, not the trainer.** The keyboard has already moved, synchronously, inside
 * the press (`lib/useQueueCard`), and the ghost is `inert` — so every row below it is live and the
 * next task can be answered while this one is still closing.
 *
 * ── Why the gap closes at all, under a policy that forbids movement ──────────────────────────
 * `src/shared/AGENTS.md` bans the jump, and this is not one: the jump it bans is movement nobody
 * asked for, arriving while a screen fills. This movement is the direct consequence of a press,
 * and it is the only evidence the list is one row shorter.
 */
const DEPARTURES: Readonly<Record<ScreenDepartureName, DepartureSpec>> = {
  rowDone: { exit: { opacity: 0, y: -5, scale: 0.975 }, contentMs: 120, collapseDelay: 20 },
  rowDefer: { exit: { opacity: 0, y: 8 }, contentMs: 140, collapseDelay: 40 },
};

/**
 * **How long the gap takes to shut — one number, in all four cases.**
 *
 * The mock's `boxMs`. A list that closes at two speeds depending on which button was pressed, or on
 * whether a sign was drawn, is a difference nobody asked to read; it was 180ms here and 120 in the
 * mock, and the mock is the one that was approved.
 */
const COLLAPSE_MS = 120;

/** Milliseconds to seconds — Motion's transition fields are seconds, the handoff's are not. */
const seconds = (ms: number): number => ms / 1000;

/**
 * The entrance step for one element, ready to spread onto a Motion component.
 *
 * Under `reducedMotion` the returned step still exists and still has an `initial` — it is simply
 * the same state as `animate`, at zero duration. Returning a step rather than `undefined` means a
 * caller cannot branch its JSX on the preference and quietly lose the element.
 */
export function screenMotion(name: ScreenMotionName, options: ScreenMotionOptions): ScreenMotionStep {
  const spec = STEPS[name];

  const ease = spec.ease ?? SCREEN_EASE;

  if (options.reducedMotion) {
    // The LAST frame, which for a keyframed step is where the list ends rather than the list. No
    // `exit` either: a step that takes no time to play takes none to be replaced.
    const rest = restingState(spec.animate);
    return {
      initial: rest,
      animate: rest,
      transition: { duration: 0, delay: 0, ease },
    };
  }

  const baseDelay = options.delay ?? spec.baseDelay;
  const delayMs = baseDelay + (options.index ?? 0) * spec.cascade;

  return {
    initial: spec.initial,
    animate: spec.animate,
    transition: {
      duration: seconds(spec.duration),
      delay: seconds(delayMs),
      ease,
      ...(spec.times === undefined ? {} : { times: spec.times }),
    },
  };
}

/** Where a step stands once it has finished — its last keyframe, or its only one. */
function restingState(animate: MotionState): MotionState {
  return Object.fromEntries(
    Object.entries(animate).map(([property, value]) => [
      property,
      typeof value === 'number' ? value : (value.at(-1) ?? 0),
    ]),
  );
}

/**
 * **The desk's deck**: the answered card leaving sideways and the next one rising behind it.
 *
 * `/zadania` is a deck, not a list. The phone's queue already moves this way and the owner asked
 * for the same gesture here on 19 September: an answered card should LEAVE, in the direction of the
 * act, so the trainer reads which button they pressed without looking at it, and the next card
 * should come up off the deck edge rather than appear.
 *
 * It is separate from `screenDeparture` above because that one is a ROW: it has a slot closing
 * under it and a sign standing in its place, and a card on a desk has neither. What they share is
 * the rule — the departure is on `EXIT_EASE`, because it accelerates away and nothing about it is
 * left to read; the arrival is on `SWAP_EASE`, because something IS.
 *
 * ── The numbers, and why these ──────────────────────────────────────────────────────────────
 *
 * | | travel | tilt | duration | starts |
 * |---|---|---|---|---|
 * | leaves right ("Gotowe") | 56px | 2deg | 190ms | on the press |
 * | leaves left ("Później") | -56px | -2deg | 190ms | on the press |
 * | next card rises | y 8 -> 0, scale .985 -> 1 | | 160ms | 80ms in |
 * | a card coming BACK (undo) | from the side it left | its own tilt | 130ms | on the press |
 *
 * The two overlap by design: the departure is still running when the arrival starts, so the whole
 * swap is **240ms** rather than 350. The tilt is two degrees because the card is 836px wide — at
 * that width a rotation the eye reads as a tilt is much smaller than it would be on a phone, and
 * four degrees looked like a fault rather than a gesture.
 *
 * Undo is the same gesture backwards and quicker, for the reason undo always is: the trainer has
 * already decided, and what they want is the card back, not a replay.
 *
 * ── What it does NOT do ─────────────────────────────────────────────────────────────────────
 * It animates opacity and transform only, so nothing is laid out again and the card's box never
 * moves; the desk's own `.stack` puts both cards in ONE grid cell, so the outgoing card cannot push
 * the incoming one. `src/shared/AGENTS.md` bans the jump, and this is not one: it is the direct
 * consequence of a press, and `e2e/tasks.spec.ts`'s cold-entry measurement stays at zero because
 * nothing here runs on arrival.
 *
 * Under `reducedMotion` there is no travel, no tilt, no rise and no time — the card is simply
 * replaced. That is what a viewer who asked for stillness is asking for, and it is a PARAMETER
 * rather than a media block because the CSS one cannot reach a Motion animation.
 */
export type DeckSide = 'right' | 'left';

const DECK = {
  travel: 56,
  tilt: 2,
  outMs: 190,
  inMs: 160,
  inDelayMs: 80,
  riseY: 8,
  riseScale: 0.985,
  undoMs: 130,
} as const;

export interface DeckSwapOptions {
  /** Which way this card goes when it is answered. "Gotowe" is right, "Później" is left. */
  readonly leaving: DeckSide;
  /**
   * The side this card is coming BACK from, for an undo, or `null` for a card rising off the deck.
   *
   * It is the arrival's own business rather than the caller's to invert, because the two halves are
   * one gesture: a card that left to the right returns from the right, and a caller free to pass
   * either side could put it back through the card it replaced.
   */
  readonly returningFrom: DeckSide | null;
  /** Required, for the reason `ScreenMotionOptions.reducedMotion` is. */
  readonly reducedMotion: boolean;
}

/** One card's whole life on the desk, ready to spread onto a Motion component. */
export interface DeckSwapStep {
  readonly initial: MotionState;
  readonly animate: MotionState;
  /**
   * Carries its OWN transition: Motion otherwise plays an exit on the arrival's timing, which for
   * a card rising off the deck includes that arrival's 80ms delay.
   *
   * Written out rather than `MotionState &`, because a record whose index signature is
   * `number | number[]` cannot also hold a transition object.
   */
  readonly exit: {
    readonly opacity: number;
    readonly x: number;
    readonly y: number;
    readonly rotate: number;
    readonly scale: number;
    readonly transition: { readonly duration: number; readonly ease: Curve };
  };
  readonly transition: { readonly duration: number; readonly delay: number; readonly ease: Curve };
}

const DECK_REST = { opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 } as const;

export function deckSwap({ leaving, returningFrom, reducedMotion }: DeckSwapOptions): DeckSwapStep {
  if (reducedMotion) {
    return {
      initial: DECK_REST,
      animate: DECK_REST,
      exit: { ...DECK_REST, opacity: 0, transition: { duration: 0, ease: EXIT_EASE } },
      transition: { duration: 0, delay: 0, ease: SWAP_EASE },
    };
  }

  const sign = leaving === 'right' ? 1 : -1;
  const back = returningFrom === null ? null : returningFrom === 'right' ? 1 : -1;

  return {
    initial:
      back === null
        ? { opacity: 0, x: 0, y: DECK.riseY, rotate: 0, scale: DECK.riseScale }
        : { opacity: 0, x: back * DECK.travel, y: 0, rotate: back * DECK.tilt, scale: 1 },
    animate: DECK_REST,
    exit: {
      opacity: 0,
      x: sign * DECK.travel,
      y: 0,
      rotate: sign * DECK.tilt,
      scale: 1,
      transition: { duration: seconds(DECK.outMs), ease: EXIT_EASE },
    },
    transition: {
      duration: seconds(back === null ? DECK.inMs : DECK.undoMs),
      // A card coming back does not wait for anything: nothing is leaving.
      delay: seconds(back === null ? DECK.inDelayMs : 0),
      ease: SWAP_EASE,
    },
  };
}

/**
 * **The auth flow's step entrance** — the D3 poster mockup's own `rise`, which is not this table's.
 *
 * `docs/design/rejestracja-premium-2026-09-21/d3-plakat-dark-losowanie.html` writes
 * `@keyframes rise{from{opacity:0;transform:translate(var(--dx,16px),9px)}}` at
 * `400ms var(--screen) calc(var(--i) * 40ms) both`, and its notes drawer states the rule the
 * numbers serve: **one orchestrated sequence per step, and the DIRECTION is carried on the X
 * axis** — forward +16px, back −16px.
 *
 * It is a function of its own rather than an entry in `STEPS`, for the reason `deckSwap` and
 * `screenDeparture` are: the direction is not a cascade index and not a delay override, and adding
 * a `direction` to `ScreenMotionOptions` would put a parameter on every entrance in the app for the
 * sake of one surface. The shape is the same — spread `initial`, `animate` and `transition` onto a
 * Motion component — so a caller reads it exactly like `screenMotion`.
 *
 * ── Why it is not `rise` with an override ────────────────────────────────────────────────────
 * `rise` above is the client-detail handoff's, at 300ms / 120ms base / 34ms cascade / y 14 and no
 * x at all. Four numbers differ and the axis differs, so reusing the name would mean one of the two
 * handoffs is not what ships. Both are recorded, each under the surface that draws it.
 *
 * Under `reducedMotion` the element is at its finished frame, with no time and no travel — the CSS
 * media block in `tokens/base.css` cannot reach a Motion animation, so the preference is a
 * parameter here as it is everywhere else in this file.
 */
export interface AuthStepEntranceOptions {
  /** Position in the step's cascade, zero-based. */
  readonly index: number;
  /**
   * Which way the flow just moved. Forward enters from the right, back from the left, so a trainer
   * reads whether they advanced or returned without looking for it.
   */
  readonly direction: 'forward' | 'back';
  /** Required, for the reason `ScreenMotionOptions.reducedMotion` is. */
  readonly reducedMotion: boolean;
}

const AUTH_STEP = { duration: 400, cascade: 40, travelX: 16, travelY: 9 } as const;

export function authStepEntrance({
  index,
  direction,
  reducedMotion,
}: AuthStepEntranceOptions): ScreenMotionStep {
  const rest = { opacity: 1, x: 0, y: 0 };

  if (reducedMotion) {
    return { initial: rest, animate: rest, transition: { duration: 0, delay: 0, ease: SCREEN_EASE } };
  }

  return {
    initial: {
      opacity: 0,
      x: direction === 'forward' ? AUTH_STEP.travelX : -AUTH_STEP.travelX,
      y: AUTH_STEP.travelY,
    },
    animate: rest,
    transition: {
      duration: seconds(AUTH_STEP.duration),
      delay: seconds(index * AUTH_STEP.cascade),
      ease: SCREEN_EASE,
    },
  };
}

/**
 * How one answered row leaves, ready to spread onto a Motion component inside an `AnimatePresence`.
 *
 * Under `reducedMotion` the row still goes — it goes in NO time. The target is unchanged, so the
 * element is removed in the frame the answer lands rather than fading out over a fifth of a second,
 * which is what a viewer who asked for stillness is asking for.
 */
export function screenDeparture(
  name: ScreenDepartureName,
  options: ScreenDepartureOptions,
): ScreenDepartureStep {
  const spec = DEPARTURES[name];
  // ONE property closes the gap, and everything that occupies space has to be inside it: the queue's
  // rows are spaced by a box INSIDE the slot rather than by a margin, a list `gap` or the slot's own
  // padding — see `QueueRow` for why, and for the measurement that ruled the padding out.
  const content = { exit: spec.exit, rest: { opacity: 1, y: 0, scale: 1 } };
  const box = { exit: { height: 0 }, rest: { height: 'auto' } };

  if (options.reducedMotion) {
    return {
      content: { ...content, transition: { duration: 0, delay: 0, ease: SWAP_EASE } },
      box: { ...box, transition: { duration: 0, delay: 0, ease: EXIT_EASE } },
      // No sign either: a viewer who asked for stillness is drawn none at all, which is
      // `model/queueClosing`'s decision and this is the shape of it in the dictionary.
      signLife: null,
    };
  }

  // A signed row runs the mock's P2 throughout — the content goes faster, and the box is released
  // in the frame the plate starts to fade rather than after it has finished. A row with nothing to
  // show keeps its own, slower fade and closes almost at once, because there is nothing to wait for.
  const contentMs = options.signed ? SIGNED.contentMs : spec.contentMs;
  const collapseDelay = options.signed ? SIGNED.collapseDelay : spec.collapseDelay;

  return {
    content: {
      ...content,
      // The CONTENT moves on the mock's `--ease-fp`, which this dictionary already holds as
      // `SWAP_EASE`; only the box closing uses the exit curve.
      transition: { duration: seconds(contentMs), delay: 0, ease: SWAP_EASE },
    },
    box: {
      ...box,
      transition: {
        duration: seconds(COLLAPSE_MS),
        delay: seconds(collapseDelay),
        ease: EXIT_EASE,
      },
    },
    signLife: options.signed
      ? {
          plate: screenMotion('signPlate', { reducedMotion: false }),
          glyph: screenMotion('signGlyph', { reducedMotion: false }),
        }
      : null,
  };
}
